import { AlertCircle, CheckCircle2, ChevronDown, Plus, Sparkles } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { useAddSkill, type JDDetail, type JDSkill } from "@/api/jd-queries";
import { ConceptsDrawer } from "@/components/jd-concept-picker";
import { DraftQuestionsDrawer, type GapSkill } from "@/components/jd-generate-form";
import { ImportanceSwitch, SKILL_COLS, SkillEditor, SkillLine, StackPicker } from "@/components/jd-skill-row";
import { Button } from "@/components/ui/button";
import { Input, Select } from "@/components/ui/input";
import { Empty, ErrorNote, Panel } from "@/components/ui/panel";
import { useDraft } from "@/lib/jd-draft-state";
import { isMappable, skillGroup, type SkillGroup } from "@/lib/jd-steps";
import { cn } from "@/lib/utils";

/**
 * Step 2, Skills — DESIGN §20.5 step 4, §20.11. The skills the bank cannot ask about yet come first,
 * each with its fixes beside it; the rest read as a plain list that opens to edit. A skill stays in
 * the section it was saved in while it is edited, so choosing a stack does not make it jump away
 * mid-task. How many questions a round asks lives on Interview mix, not here.
 */

const CATEGORIES = ["technical", "domain", "soft", "other"] as const;
const cap = "text-bank-muted mb-1 block text-[11.5px] font-medium";

/** What is wrong with a skill that needs attention, or what the unsaved edit will do about it. */
function attentionLine(skill: JDSkill, saved: JDSkill): { text: string; fixed: boolean } {
  if (!isMappable(skill))
    return { text: `Now a ${skill.category} skill — it needs no stack once you save.`, fixed: true };
  if (skill.status === "ignored")
    return { text: "Will be left out of interviews once you save.", fixed: true };
  if (skill.stack_path && skill.stack_path !== saved.stack_path)
    return { text: "Stack chosen — its questions are counted once you save.", fixed: true };
  if (skill.stack_path)
    return {
      text: skill.concepts.length
        ? `Mapped to ${skill.stack_path}, but none of its chosen concepts has questions yet.`
        : `Mapped to ${skill.stack_path}, which has no questions yet.`,
      fixed: false,
    };
  if (skill.entity)
    return {
      text: `Matched by the keyword “${skill.entity}”, but no question in the bank names it.`,
      fixed: false,
    };
  return { text: "No stack chosen, so the bank has nothing to ask about it.", fixed: false };
}

function AttentionItem({
  jd,
  saved,
  open,
  onToggle,
  canManage,
  onConcepts,
  onDraft,
}: {
  jd: JDDetail;
  saved: JDSkill;
  open: boolean;
  onToggle: () => void;
  canManage: boolean;
  onConcepts: () => void;
  onDraft: () => void;
}) {
  const draft = useDraft();
  const skill = draft.skill(saved);
  const line = attentionLine(skill, saved);
  const ignored = skill.status === "ignored";
  return (
    <div className="border-bank-border border-t">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2.5 px-4 py-3">
        <div className="min-w-0 flex-[1_1_300px]">
          <p className="flex flex-wrap items-baseline gap-x-2">
            <span className={cn("text-sm font-semibold", ignored && "text-bank-muted line-through")}>
              {skill.name}
            </span>
            <span className="text-bank-muted text-xs capitalize">
              {skill.category} · {skill.importance}
            </span>
          </p>
          {skill.raw_text !== skill.name && (
            <p className="text-bank-muted mt-0.5 text-xs">“{skill.raw_text}”</p>
          )}
          <p className={cn("mt-1 text-[12.5px]", line.fixed ? "text-bank-muted" : "text-bank-attention-ink")}>
            {line.text}
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            {ignored || !isMappable(skill) ? (
              <Button size="sm" onClick={() => draft.resetSkill(skill.id)}>
                Undo
              </Button>
            ) : (
              <>
                <StackPicker jd={jd} skill={skill} onConcepts={onConcepts} />
                <Button size="sm" onClick={onDraft}>
                  <Sparkles className="size-3.5" aria-hidden="true" />
                  Draft questions
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => draft.patchSkill(skill.id, { status: "ignored" })}
                >
                  Ignore
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant="ghost"
              aria-expanded={open}
              aria-label={`Type and importance of ${skill.name}`}
              title="Type and importance"
              onClick={onToggle}
            >
              <ChevronDown
                className={cn("size-4 transition-transform", open && "rotate-180")}
                aria-hidden="true"
              />
            </Button>
          </div>
        )}
      </div>
      {open && (
        <SkillEditor jd={jd} skill={skill} canManage={canManage} withStack={false} onConcepts={onConcepts} />
      )}
    </div>
  );
}

function AddSkill({ jd, onClose }: { jd: JDDetail; onClose: () => void }) {
  const add = useAddSkill(jd.jd.id);
  const [text, setText] = useState("");
  const [importance, setImportance] = useState<JDSkill["importance"]>("must");
  const [category, setCategory] = useState<JDSkill["category"]>("technical");
  return (
    <Panel className="px-4 py-3">
      <form
        className="flex flex-wrap items-end gap-x-4 gap-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) add.mutate({ text, importance, category }, { onSuccess: () => setText("") });
        }}
      >
        <div className="flex-[1_1_260px]">
          <label htmlFor="new-skill" className={cap}>
            A skill the AI missed
          </label>
          <Input
            id="new-skill"
            autoFocus
            placeholder="e.g. Redux"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <div>
          <span className={cap}>Importance</span>
          <ImportanceSwitch label="Importance of the new skill" value={importance} onChange={setImportance} />
        </div>
        <div>
          <label htmlFor="new-skill-type" className={cap}>
            Type
          </label>
          <Select
            id="new-skill-type"
            className="w-32 capitalize"
            value={category}
            onChange={(e) => setCategory(e.target.value as JDSkill["category"])}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={!text.trim() || add.isPending}>
            <Plus className="size-4" aria-hidden="true" />
            Add
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </form>
      <p className="text-bank-muted mt-2 text-xs">
        Saved straight away, and mapped to a stack like the rest.
      </p>
      <ErrorNote error={add.error} />
    </Panel>
  );
}

/** A section that is fine as it is, folded away until somebody wants it. */
function Fold({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <details className="group border-bank-border bg-bank-surface overflow-hidden rounded-lg border">
      <summary className="hover:bg-bank-surface-2 flex cursor-pointer list-none items-center gap-2 px-4 py-2.5">
        <ChevronDown
          className="text-bank-muted size-4 -rotate-90 transition-transform group-open:rotate-0"
          aria-hidden="true"
        />
        <span className="text-sm font-semibold">{title}</span>
        <span className="text-bank-muted text-[12.5px]">— {note}</span>
      </summary>
      <div className="border-bank-border overflow-x-auto border-t">{children}</div>
    </details>
  );
}

export function SkillsStep({ jd, canManage }: { jd: JDDetail; canManage: boolean }) {
  const draft = useDraft();
  const [open, setOpen] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [conceptsFor, setConceptsFor] = useState<number | null>(null);
  const [draftFor, setDraftFor] = useState<GapSkill | null>(null);

  const inGroup = (g: SkillGroup) => jd.skills.filter((sk) => skillGroup(sk) === g);
  const attention = inGroup("attention");
  const mapped = inGroup("mapped");
  const unmappable = inGroup("unmappable");
  const ignored = inGroup("ignored");
  const toggle = (id: number) => setOpen((o) => (o === id ? null : id));
  const conceptSaved = jd.skills.find((sk) => sk.id === conceptsFor);
  const conceptSkill = conceptSaved ? draft.skill(conceptSaved) : null;

  const draftQuestions = (saved: JDSkill) => {
    const shown = draft.skill(saved);
    setDraftFor({
      jdId: jd.jd.id,
      skillId: saved.id,
      name: shown.name,
      context: `${shown.importance} · “${shown.raw_text}” · ${shown.stack_path ? `mapped to ${shown.stack_path}` : "not mapped"}`,
      stack: shown.stack_path,
      stacks: shown.category === "technical" ? jd.technical_stacks : jd.domain_stacks,
    });
  };
  const line = (saved: JDSkill) => (
    <SkillLine
      key={saved.id}
      jd={jd}
      saved={saved}
      open={open === saved.id}
      onToggle={() => toggle(saved.id)}
      canManage={canManage}
      onConcepts={() => setConceptsFor(saved.id)}
    />
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-[13px]">
          Each technical or domain skill points at a stack in the bank — that is where its interview questions
          come from. <span className="text-bank-muted">Open a skill to change it.</span>
        </p>
        {canManage && !adding && (
          <Button onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Add a skill
          </Button>
        )}
      </div>
      {adding && <AddSkill jd={jd} onClose={() => setAdding(false)} />}

      {attention.length > 0 ? (
        <section
          aria-label="Needs attention"
          className="border-bank-attention bg-bank-surface overflow-hidden rounded-lg border"
        >
          <div className="bg-bank-attention-wash text-bank-attention-ink flex flex-wrap items-center gap-x-2 px-4 py-2.5">
            <AlertCircle className="text-bank-attention size-4" aria-hidden="true" />
            <h3 className="text-sm font-semibold">Needs attention · {attention.length}</h3>
            <span className="text-[12.5px]">
              — the bank can't ask about {attention.length === 1 ? "this skill" : "these skills"} yet
            </span>
          </div>
          {attention.map((saved) => (
            <AttentionItem
              key={saved.id}
              jd={jd}
              saved={saved}
              open={open === saved.id}
              onToggle={() => toggle(saved.id)}
              canManage={canManage}
              onConcepts={() => setConceptsFor(saved.id)}
              onDraft={() => draftQuestions(saved)}
            />
          ))}
        </section>
      ) : (
        <p className="flex items-center gap-2 text-[13px]">
          <CheckCircle2 className="text-bank-good size-4" aria-hidden="true" />
          Every technical and domain skill has questions in the bank.
        </p>
      )}

      <Panel className="overflow-hidden">
        <h3 className="border-bank-border border-b px-4 py-2.5 text-sm font-semibold">
          Mapped to the bank <span className="text-bank-muted font-normal">· {mapped.length}</span>
        </h3>
        <div className="overflow-x-auto">
          {mapped.length === 0 ? (
            <Empty>No skill points at a stack with questions yet.</Empty>
          ) : (
            <>
              <div
                className={cn(
                  SKILL_COLS,
                  "text-bank-muted px-4 pt-2 pb-1 font-mono text-[10.5px] tracking-[0.07em] uppercase",
                )}
              >
                <span>Skill</span>
                <span>Type</span>
                <span>Importance</span>
                <span>Stack</span>
                <span className="text-right">Questions</span>
                <span />
              </div>
              {mapped.map(line)}
            </>
          )}
        </div>
      </Panel>

      {unmappable.length > 0 && (
        <Fold
          title={`Soft & other · ${unmappable.length}`}
          note="no stack needed; the soft-skill and problem-solving parts of the interview cover these"
        >
          {unmappable.map(line)}
        </Fold>
      )}
      {ignored.length > 0 && (
        <Fold title={`Ignored · ${ignored.length}`} note="left out of interviews and of the warnings">
          {ignored.map(line)}
        </Fold>
      )}

      <p className="text-bank-muted text-xs">
        A stack you choose is remembered as a{" "}
        <Link to="/jd-aliases" className="text-bank-accent-ink hover:underline">
          skill alias
        </Link>
        , so the next JD that words the skill the same way maps on its own. Gaps across every JD are on the{" "}
        <Link to="/jd-gaps" className="text-bank-accent-ink hover:underline">
          Gap report
        </Link>
        .
      </p>

      <ConceptsDrawer
        skill={conceptSkill}
        stackChanged={conceptSkill !== null && conceptSkill.stack_path !== conceptSaved?.stack_path}
        onClose={() => setConceptsFor(null)}
      />
      <DraftQuestionsDrawer gap={draftFor} onClose={() => setDraftFor(null)} />
    </div>
  );
}
